import type { PlaylistOptions } from '@/components/build/types'

// Mirrors Api::PlaylistBuilderController::DISCIPLINES -- the builder rejects
// anything else, so never link to it.
const BUILDABLE = new Set(['cycling', 'running', 'walking', 'cardio', 'strength', 'yoga', 'pilates', 'stretching', 'meditation'])
const TYPICAL_MINUTES = 30

export interface Recipe {
  discipline: string
  minutes: number
  theme: string | null
}

// The workout + length to build for an instructor: the discipline they teach
// most (or, for a theme, the one they teach it in most) at the length they
// teach most -- so the ride on the other end is built from real history.
// null when nothing buildable is known (the link is then simply not shown).
export function pickRecipe(options: PlaylistOptions | null, theme: string | null = null): Recipe | null {
  if (!options) return null

  const candidates = options.disciplines.filter((d) => BUILDABLE.has(d.key))
  const scored = candidates
    .map((d) => ({ d, weight: theme ? (d.themes.find((t) => t.name.toLowerCase() === theme.toLowerCase())?.rides ?? 0) : d.rides }))
    .filter((c) => c.weight > 0)
    .sort((a, b) => b.weight - a.weight)
  const best = scored[0]?.d
  if (!best) return null

  const minutes =
    [...best.durations]
      .filter((d) => d.minutes >= 5 && d.minutes <= 90)
      .sort((a, b) => b.rides - a.rides || Math.abs(a.minutes - TYPICAL_MINUTES) - Math.abs(b.minutes - TYPICAL_MINUTES))[0]?.minutes ??
    TYPICAL_MINUTES

  const canonicalTheme = theme ? (best.themes.find((t) => t.name.toLowerCase() === theme.toLowerCase())?.name ?? null) : null
  return { discipline: best.key, minutes, theme: canonicalTheme }
}

// The builder's whole recipe lives in its URL (see BuildPlaylistPage).
export function buildUrl(instructorId: string, recipe: Recipe, extras: { adventure?: number; seed?: number } = {}): string {
  const query = new URLSearchParams({ i: instructorId, d: recipe.discipline, m: String(recipe.minutes) })
  if (recipe.theme) query.set('t', recipe.theme)
  if (extras.adventure !== undefined) query.set('a', extras.adventure.toFixed(2))
  if (extras.seed !== undefined) query.set('s', String(extras.seed))
  return `/build?${query}`
}
